import React from "react";
import { useState, useEffect } from "react";
import './login.css';
import { Link, Navigate, useNavigate, useSearchParams  } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { nativeGoogleSignIn } from "../../services/capacitor";
import google from  '../../assets/google-icon-logo.svg';

const Login = ( { isLoggedIn, onLogin } ) => {
  const [loginFormData, setLoginFormData] = useState({email: "", password: ""});
  const [signupFormData, setSignupFormData] = useState({userName: "", email: "", password: "", confirmPassword: "", phoneNumber: "", gender: 1, referralCode: ""});
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleLoginChange = (event) => {
    setError(''); // Reset error message
    setEmailError(''); // Reset error message
    setPasswordError(''); // Reset error message

    const { name, value } = event.target;
    setLoginFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleSignupChange = (event) => {
    setError(''); // Reset error message
    setEmailError(''); // Reset error message
    setPasswordError(''); // Reset error message

    const { name, value } = event.target;
    setSignupFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setError(''); // Reset error message
    setEmailError(''); // Reset error message
    setPasswordError(''); // Reset error message

    // Frontend validation
    const isEmailValid = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,4}$/.test(loginFormData.email);
    if (!loginFormData.email || !isEmailValid) {
      setEmailError('Please enter a valid email');
      return;
    }
    // Frontend validation
    const strongPassword = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!strongPassword.test(loginFormData.password)) {
      setPasswordError('Password must be at least 8 characters with one uppercase, one lowercase, and one number');
      return;
    }

    try {
        const response = await axios.post('/api/v1/login', loginFormData);

        onLogin(response.data.user);
        navigate('/feed');
    } catch (error) {
        if (error.response) {
          setError(error.response.data.message);
        } else {
            setError('An error occurred on log in. Please try again');
        }
    }
  };

  const handleSignupSubmit = async (event) => {
    event.preventDefault();
    setError(''); // Reset error message
    setEmailError(''); // Reset error message
    setPasswordError(''); // Reset error message

    // Frontend validation
    if (!signupFormData.userName || !signupFormData.email || !signupFormData.password || !signupFormData.confirmPassword || !signupFormData.phoneNumber) {
      setError('All fields are required!');
      return;
    }

    // Frontend validation
    const isEmailValid = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,4}$/.test(signupFormData.email);
    if (!signupFormData.email || !isEmailValid) {
      setEmailError('Please enter a valid email');
      return;
    }
    // Frontend validation
    const strongPassword = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!strongPassword.test(signupFormData.password)) {
      setPasswordError('Password must be at least 8 characters with one uppercase, one lowercase, and one number');
      return;
    }
    // Frontend validation
    if (signupFormData.password !== signupFormData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
        const response = await axios.post('/api/v1/signup', signupFormData);

        navigate('/message', { state: { text: response.data.message, type: 'success', from: '/login' } });
    } catch (error) {
        if (error.response) {
          navigate('/message', { state: { text: error.response.data.message, type: 'error', from: '/login' } });
          setError(error.response.data.message);
        } else {
          navigate('/message', { state: { text: "An error occurred on Signup. Please try again", type: 'error', from: '/login' } });
          setError('An error occurred on Signup. Please try again');
        }
    }
  };

  const handleGoogleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    // Carry a referral code (from a ?ref= link or manual entry) so Google
    // signups are attributed to the referrer too.
    const ref = signupFormData.referralCode ? signupFormData.referralCode.trim().toUpperCase() : "";

    if (Capacitor.isNativePlatform()) {
      // Native Google Sign-In: the OS account picker opens in-app (no browser,
      // no redirect, no bridge page). We get a Google ID token and hand it to
      // the server to verify + create the session.
      try {
        const idToken = await nativeGoogleSignIn();
        const { data } = await axios.post('/api/v1/auth/google/native', {
          idToken,
          ...(ref ? { referralCode: ref } : {}),
        });
        if (data && data.user) {
          onLogin(data.user);
          navigate('/feed');
        }
      } catch (err) {
        // User cancelling the picker throws too — keep the message soft.
        setError(err?.response?.data?.message || 'Google sign-in was cancelled or failed. Please try again.');
      }
    } else {
      const refQuery = ref ? `?ref=${encodeURIComponent(ref)}` : "";
      window.location.href = `/api/v1/auth/google${refQuery}`;
    }
  }

  useEffect(() => {
    const handleGoogleAuthCallback = async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const token = queryParams.get('token');
      if (!token) return;

      // Strip the code from the URL immediately (avoid it lingering in history).
      window.history.replaceState({}, document.title, window.location.pathname);

      try {
        const { data } = await axios.post('/api/v1/auth/exchange', { token });
        if (data && data.user) {
          onLogin(data.user);
          navigate('/feed');
        }
      } catch (error) {
        console.error('Error during Google callback:', error);
      }
    };

    handleGoogleAuthCallback();
  }, [navigate, onLogin]);

  // Handle email verification redirect
  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === 'true') {
      // Server already logged the user in via session — fetch current user
      axios.get('/api/v1/login')
        .then((res) => {
          if (res.data.user) {
            onLogin(res.data.user);
            navigate('/feed');
          } else {
            setVerifyMessage('Email verified! Please log in.');
          }
        })
        .catch(() => {
          setVerifyMessage('Email verified! Please log in.');
        });
    } else if (verified === 'already') {
      setVerifyMessage('Email is already verified. Please log in.');
    } else if (verified === 'false') {
      const reason = searchParams.get('reason');
      if (reason === 'expired') {
        setVerifyMessage('Verification link expired. Please sign up again.');
      } else {
        setVerifyMessage('Verification failed. Please try again.');
      }
    }
  }, [searchParams, navigate, onLogin]);

  // Pre-fill the referral code from a shared link (?ref=CODE) and open the
  // signup form so referred friends land in the right place.
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const ref = queryParams.get('ref');
    if (ref) {
      setSignupFormData((prev) => ({ ...prev, referralCode: ref.trim().toUpperCase() }));
      setIsSignUpMode(true);
    }
  }, []);

  const handleSignUpClick = () => {
    setIsSignUpMode(true);
  };

  const handleLoginClick = () => {
    setIsSignUpMode(false);
  };

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="sr-login">
      <Helmet>
        <title>Login or Sign Up - SastiRide</title>
        <meta name="description" content="Log in or create your SastiRide account to find affordable rides and carpool partners across Pakistan." />
        <link rel="canonical" href="https://sastiride.com/login" />
      </Helmet>
      <div className="sr-login__top">
        <h1 className="sr-login__title">{isSignUpMode ? 'Get on board!' : 'Welcome back!'}</h1>
        <p className="sr-login__subtitle">{isSignUpMode ? "It's quick and easy." : 'Explore rides.'}</p>
      </div>

      <div className="sr-login__body">
        <div className="sr-login__form-wrap">
          <div className="sr-login__slide-controls">
            <input type="radio" name="slide" id="login" checked={!isSignUpMode} onChange={handleLoginClick} />
            <input type="radio" name="slide" id="signup" checked={isSignUpMode} onChange={handleSignUpClick} />
            <label htmlFor="login" className="slide" onClick={handleLoginClick}> Login </label>
            <label htmlFor="signup" className="slide" onClick={handleSignUpClick}> Signup </label>
            <div className={`slider-tab ${isSignUpMode ? 'shift-right-slider' : ''}`}></div>
          </div>

          <div className={`sr-login__form-container ${isSignUpMode ? 'shift-right' : ''}`}>
            {/* Login Form */}
            <div className="sr-login__form-panel">
              {verifyMessage && <p className="sr-login__verify-msg">{verifyMessage}</p>}
              <form onSubmit={handleLoginSubmit} className={`${isSignUpMode ? 'hide' : ''}`} >
                <input type="email" id="email" name="email" value={loginFormData.email} onChange={handleLoginChange} placeholder="Email"/>
                {emailError && <label htmlFor="email" className="sr-login__error">{emailError}</label>}
                <input type="password" id="password" name="password" value={loginFormData.password} onChange={handleLoginChange} placeholder="Password"/>
                {passwordError && <label htmlFor="password" className="sr-login__error">{passwordError}</label>}
                {error && <label className="sr-login__error">{error}</label>}
                <button type="submit">Log in</button>

                <div className="sr-login__separator"><p>or</p></div>

                <Link to="/" onClick={handleGoogleSubmit}>
                  <div className="sr-login__google-btn">
                    <img src={google} alt="logo" />Log in with Google
                  </div>
                </Link>

                <p onClick={handleSignUpClick}>Don't have an account? <Link>Sign Up</Link></p>
                <p className="sr-login__forgot"><Link to="/forgot-password">Forgot Password?</Link></p>
              </form>
            </div>

            {/* Signup Form */}
            <div className="sr-login__form-panel">
              <form onSubmit={handleSignupSubmit} className={`${isSignUpMode ? '' : 'hide'}`} >
                <input type="text" id="userName" name="userName" value={signupFormData.userName} onChange={handleSignupChange} placeholder="User Name"/>
                <input type="email" id="email" name="email" value={signupFormData.email} onChange={handleSignupChange} placeholder="Email"/>
                {emailError && <label htmlFor="email" className="sr-login__error">{emailError}</label>}
                <input type="password" id="password" name="password" value={signupFormData.password} onChange={handleSignupChange} placeholder="Password"/>
                {passwordError && <label htmlFor="password" className="sr-login__error">{passwordError}</label>}
                <input type="password" id="confirmPassword" name="confirmPassword" value={signupFormData.confirmPassword} onChange={handleSignupChange} placeholder="Re-enter password"/>
                {passwordError && <label htmlFor="confirmPassword" className="sr-login__error">{passwordError}</label>}
                <input type="tel" id="phoneNumber" name="phoneNumber" value={signupFormData.phoneNumber} onChange={handleSignupChange} placeholder="Phone number"/>

                <select id="gender" name="gender" value={signupFormData.selectedValue} onChange={handleSignupChange}>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="custom">Custom</option>
                </select>

                <input type="text" id="referralCode" name="referralCode" value={signupFormData.referralCode} onChange={handleSignupChange} placeholder="Referral code (optional)"/>

                {error && <label className="sr-login__error">{error}</label>}

                <button type="submit">Sign Up</button>

                <div className="sr-login__separator"><p>or</p></div>

                <Link to="/" onClick={handleGoogleSubmit}>
                  <div className="sr-login__google-btn">
                    <img src={google} alt="logo" />Sign up with Google
                  </div>
                </Link>
                <p onClick={handleLoginClick}>Already have an account? <Link>Log in</Link></p>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
